import React, { useMemo } from 'react';
import Select from 'react-select';
import { rgbaFromPrimary } from '../../utils/heroThemeColors';

/**
 * Estilos extras para Select no layout editorial: evita overflow horizontal na coluna estreita do grid.
 */
function mergeEditorialSelectStyles(customStyles) {
  if (!customStyles) return customStyles;
  return {
    ...customStyles,
    container: (provided, state) => ({
      ...(customStyles.container ? customStyles.container(provided, state) : provided),
      width: '100%',
      minWidth: 0
    }),
    control: (provided, state) => ({
      ...(customStyles.control ? customStyles.control(provided, state) : provided),
      minWidth: 0
    }),
    valueContainer: (provided, state) => ({
      ...(customStyles.valueContainer ? customStyles.valueContainer(provided, state) : provided),
      overflow: 'hidden'
    }),
    singleValue: (provided, state) => ({
      ...(customStyles.singleValue ? customStyles.singleValue(provided, state) : provided),
      maxWidth: '100%'
    })
  };
}

/** Selects legíveis sobre vidro escuro (hero luxo). */
function mergeLuxurySelectStyles(customStyles, primaryColor) {
  const selectedTint = rgbaFromPrimary(primaryColor, 0.38);
  const base = mergeEditorialSelectStyles(customStyles) || customStyles || {};
  const glass = {
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderColor: 'rgba(255,255,255,0.22)',
    color: '#f5f0e8',
    boxShadow: 'none'
  };
  return {
    ...base,
    control: (provided, state) => {
      const p = base.control ? base.control(provided, state) : provided;
      return { ...p, ...glass, minHeight: 46, height: 46 };
    },
    singleValue: (provided, state) => ({
      ...(base.singleValue ? base.singleValue(provided, state) : provided),
      color: '#faf8f4'
    }),
    placeholder: (provided, state) => ({
      ...(base.placeholder ? base.placeholder(provided, state) : provided),
      color: 'rgba(250,248,244,0.45)'
    }),
    menu: (provided, state) => ({
      ...(base.menu ? base.menu(provided, state) : provided),
      backgroundColor: '#1a1a1c',
      border: '1px solid rgba(255,255,255,0.12)'
    }),
    option: (provided, state) => {
      const p = base.option ? base.option(provided, state) : provided;
      return {
        ...p,
        backgroundColor: state.isSelected
          ? selectedTint
          : state.isFocused
            ? 'rgba(255,255,255,0.06)'
            : 'transparent',
        color: '#f5f0e8'
      };
    },
    indicatorSeparator: (provided) => ({ ...provided, backgroundColor: 'rgba(255,255,255,0.15)' }),
    dropdownIndicator: (provided) => ({ ...provided, color: 'rgba(250,248,244,0.55)' })
  };
}

/**
 * Barra de busca do hero (Home) — compartilhada entre layout Padrão e Editorial.
 * @param {boolean} twoRowActions — layout editorial: 3 faixas (busca | selects em grelha | botões), à prova de coluna estreita.
 * @param {boolean} luxuryDark — campos claros sobre vidro escuro (hero luxo).
 */
export default function HomeHeroSearchForm({
  searchParams,
  setSearchParams,
  typeOptions,
  purposeOptions,
  customStyles,
  primaryColor,
  onSubmit,
  navigate,
  formClassName = 'bg-white p-3 sm:p-4 rounded-lg shadow-lg flex flex-col gap-3 max-w-6xl mx-auto',
  formStyle = {},
  menuPortalTarget = null,
  twoRowActions = false,
  luxuryDark = false
}) {
  const selectProps = menuPortalTarget
    ? { menuPortalTarget, menuPosition: 'fixed' }
    : {};

  const selectStyles = useMemo(() => {
    if (!twoRowActions) return customStyles;
    if (luxuryDark) return mergeLuxurySelectStyles(customStyles, primaryColor);
    return mergeEditorialSelectStyles(customStyles);
  }, [twoRowActions, luxuryDark, customStyles, primaryColor]);

  const luxuryFocusRing = useMemo(
    () => ({ '--tw-ring-color': rgbaFromPrimary(primaryColor, 0.5) }),
    [primaryColor]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(e);
  };

  const maisFiltrosBtn = luxuryDark ? (
    <button
      type="button"
      onClick={() => navigate('/propriedades?showFilters=true')}
      className="w-full shrink-0 whitespace-nowrap rounded-lg border border-white/25 bg-transparent px-4 text-sm font-medium text-[#f5f0e8] transition-colors duration-200 hover:bg-white/10 sm:w-[160px] sm:text-base h-[46px]"
    >
      Mais Filtros
    </button>
  ) : (
    <button
      type="button"
      onClick={() => navigate('/propriedades?showFilters=true')}
      className="w-full sm:w-[160px] px-4 h-[46px] rounded-lg transition-all duration-200 text-sm sm:text-base font-medium whitespace-nowrap shrink-0"
      style={{
        backgroundColor: 'var(--btn-outline-bg, #ffffff)',
        color: 'var(--btn-outline-text, #3B82F6)',
        border: '1px solid var(--btn-outline-border, #3B82F6)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--btn-outline-hover-bg, #3B82F6)';
        e.currentTarget.style.color = 'var(--btn-outline-hover-text, #ffffff)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--btn-outline-bg, #ffffff)';
        e.currentTarget.style.color = 'var(--btn-outline-text, #3B82F6)';
      }}
    >
      Mais Filtros
    </button>
  );

  const buscarBtn = (
    <button
      type="submit"
      className="w-full sm:w-[140px] px-6 h-[46px] rounded-lg transition-colors duration-200 text-sm sm:text-base font-medium whitespace-nowrap shrink-0 hover:opacity-90"
      style={{
        backgroundColor: 'var(--primary-color, #3B82F6)',
        color: 'var(--secondary-color, #ffffff)'
      }}
    >
      Buscar
    </button>
  );

  /** Layout editorial: nunca coloca busca + 2 selects na mesma linha (evita estouro na meia-largura do grid). */
  if (twoRowActions) {
    return (
      <form
        onSubmit={handleSubmit}
        className={`${formClassName} min-w-0 max-w-full overflow-x-clip box-border`}
        style={formStyle}
      >
        <div className="flex flex-col gap-4 w-full min-w-0">
          <div className="w-full min-w-0">
            <input
              type="text"
              placeholder="Nome, código, cidade ou bairro..."
              className={
                luxuryDark
                  ? 'box-border h-[46px] w-full min-w-0 rounded-lg border border-white/20 bg-white/[0.07] px-3 text-sm text-[#faf8f4] placeholder:text-stone-500/70 focus:outline-none focus:ring-1 sm:text-base'
                  : 'w-full min-w-0 border border-[#cfd4dd] rounded-lg px-3 h-[46px] focus:outline-none focus:ring-0 text-sm sm:text-base box-border'
              }
              style={luxuryDark ? luxuryFocusRing : { '--tw-border-opacity': '1' }}
              onFocus={
                luxuryDark
                  ? undefined
                  : (e) => {
                      e.target.style.borderColor = primaryColor || 'var(--primary-color, #000000)';
                    }
              }
              onBlur={
                luxuryDark
                  ? undefined
                  : (e) => {
                      e.target.style.borderColor = '#cfd4dd';
                    }
              }
              value={searchParams.search}
              onChange={(e) => setSearchParams((prev) => ({ ...prev, search: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full min-w-0">
            <div className="min-w-0 w-full">
              <Select
                value={typeOptions.find((opt) => opt.value === searchParams.type)}
                onChange={(opt) => setSearchParams((prev) => ({ ...prev, type: opt?.value || '' }))}
                options={typeOptions}
                styles={selectStyles}
                placeholder="Tipo de Imóvel"
                className="w-full"
                isSearchable={false}
                hasValue={!!searchParams.type}
                {...selectProps}
              />
            </div>
            <div className="min-w-0 w-full">
              <Select
                value={purposeOptions.find((opt) => opt.value === searchParams.purpose)}
                onChange={(opt) =>
                  setSearchParams((prev) => ({ ...prev, purpose: opt?.value || '' }))
                }
                options={purposeOptions}
                styles={selectStyles}
                placeholder="Finalidade"
                className="w-full"
                isSearchable={false}
                hasValue={!!searchParams.purpose}
                {...selectProps}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:justify-end w-full min-w-0 pt-0.5">
            {maisFiltrosBtn}
            {buscarBtn}
          </div>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={formClassName}>
      <div className="flex flex-col lg:flex-row gap-3">
        <div className="w-full lg:w-[280px]">
          <input
            type="text"
            placeholder="Nome, código, cidade ou bairro..."
            className="w-full border border-[#cfd4dd] rounded-lg px-3 h-[46px] focus:outline-none focus:ring-0 text-sm sm:text-base"
            style={{ '--tw-border-opacity': '1' }}
            onFocus={(e) => {
              e.target.style.borderColor = primaryColor || 'var(--primary-color, #000000)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#cfd4dd';
            }}
            value={searchParams.search}
            onChange={(e) => setSearchParams((prev) => ({ ...prev, search: e.target.value }))}
          />
        </div>

        <div className="flex flex-col md:flex-row lg:flex-row gap-3 flex-1 min-w-0">
          <Select
            value={typeOptions.find((opt) => opt.value === searchParams.type)}
            onChange={(opt) => setSearchParams((prev) => ({ ...prev, type: opt?.value || '' }))}
            options={typeOptions}
            styles={customStyles}
            placeholder="Tipo de Imóvel"
            className="w-full md:flex-1 min-w-0"
            isSearchable={false}
            hasValue={!!searchParams.type}
            {...selectProps}
          />

          <Select
            value={purposeOptions.find((opt) => opt.value === searchParams.purpose)}
            onChange={(opt) => setSearchParams((prev) => ({ ...prev, purpose: opt?.value || '' }))}
            options={purposeOptions}
            styles={customStyles}
            placeholder="Finalidade"
            className="w-full md:flex-1 min-w-0"
            isSearchable={false}
            hasValue={!!searchParams.purpose}
            {...selectProps}
          />

          {maisFiltrosBtn}
        </div>

        {buscarBtn}
      </div>
    </form>
  );
}
